<script setup lang="ts">
import SquaredIconButton from '@/components/assistant/SquaredIconButton.vue'
import SuggestionMenu from '@/components/assistant/chat/SuggestionMenu.vue'
import suggestionsGroups from '@/components/assistant/chat/suggestions'
import type {Suggestion} from '@/components/assistant/types'
import {computed, ref, watch} from 'vue'
import {useAssistantViewport} from '@/components/assistant/composables/useAssistantViewport'
import {useI18n} from 'vue-i18n'
import {useUserStore} from '@/stores/user'

const emits = defineEmits<{
  select: [suggestion: Suggestion]
}>()

const props = defineProps<{
  disabled?: boolean
  open?: boolean
}>()

const userStore = useUserStore()
const {isMobileVersion} = useAssistantViewport()
const {t} = useI18n()
const visible = ref(props.open ?? false)

const suggestionsGroupsForLanguage = computed(() => suggestionsGroups[userStore.language.toLowerCase()] ?? suggestionsGroups.de)

watch(
  () => props.open,
  (shouldOpen) => {
    if (shouldOpen == true) {
      visible.value = true
    }
  },
  {immediate: true}
)

function onSelect(suggestion: Suggestion) {
  emits('select', suggestion)
}

function toggleVisible() {
  visible.value = !visible.value
}
</script>

<template>
  <div class="flex flex-row gap-2 items-end">
    <SquaredIconButton
      v-if="!visible"
      :disabled="disabled"
      icon-classes="fa-solid fa-lightbulb"
      variant="info"
      @click="toggleVisible"
    />

    <template v-else>
      <SquaredIconButton icon-classes="fa-solid fa-xmark" variant="light" @click="toggleVisible" />

      <SuggestionMenu
        v-if="isMobileVersion"
        :disabled="disabled"
        :label="t('assistant.chat.suggestions.mobileMenuLabel')"
        :suggestions="new Map(suggestionsGroupsForLanguage.map((group) => [group.label, group.suggestions]))"
        @select="onSelect"
      ></SuggestionMenu>

      <div v-else class="flex flex-wrap gap-x-2 gap-y-1">
        <SuggestionMenu
          v-for="group in suggestionsGroupsForLanguage"
          :key="group.label"
          :disabled="disabled"
          :label="group.label"
          :suggestions="group.suggestions"
          @select="onSelect"
        ></SuggestionMenu>
      </div>
    </template>
  </div>
</template>
